import React from 'react';
import { Card, CardContent, CardFooter } from './ui/card';
import { Star } from 'lucide-react';
import { cn } from '../lib/utils';
import { useLanguage } from '../contexts/LanguageContext';

const GoogleIcon: React.FC = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const StarRating: React.FC<{ rating: number }> = ({ rating }) => (
  <div className="flex items-center">
    {[...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={cn(
          'h-5 w-5',
          i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
        )}
      />
    ))}
  </div>
);

const TestimonialCard: React.FC<{
  content: string;
  author: string;
  role: string;
  source: string;
  rating: number;
}> = ({ content, author, role, source, rating }) => {
  const { t } = useLanguage();
  
  return (
    <Card className="h-full transition-all duration-300 hover:shadow-2xl hover:-translate-y-2">
      <CardContent className="p-8 flex flex-col h-full">
        <div className="text-7xl text-blue-200 font-serif leading-none mb-4">"</div>
        <div className="flex-grow mb-6">
          <StarRating rating={rating} />
          <p className="text-gray-700 mt-4 text-lg">{content}</p>
        </div>
        <CardFooter className="pt-6 border-t border-gray-100 mt-auto px-0">
          <div className="flex items-center justify-between w-full">
            <div>
              <p className="font-bold text-gray-900">{author}</p>
              <p className="text-sm text-gray-500">{role}</p>
            </div>
            {source === 'Google' && (
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <GoogleIcon />
                <span>{t('testimonialsFrom')}</span>
              </div>
            )}
          </div>
        </CardFooter>
      </CardContent>
    </Card>
  );
};

const Testimonials: React.FC = () => {
  const { t } = useLanguage();
  
  const googleReviews = [
    {
      content:
        '悉尼最好的搬家公司！搬运非常快 而且能搬别的搬家公司办不好的重物。价格也非常实惠！必须推给所有人',
      author: 'Eric Zhang',
      role: '7个月前',
      source: 'Google',
      rating: 5,
    },
    {
      content:
        '服务非常专业，人员态度友好。我们有很多贵重物品，他们都小心处理，没有任何损坏。价格也很合理，强烈推荐！',
      author: '李梅',
      role: '5个月前',
      source: 'Google',
      rating: 5,
    },
    {
      content:
        '从悉尼搬到墨尔本，全程非常顺利。搬家团队准时到达，高效装车，所有家具都包装得很好。到达后快速卸货和安装，超出了我的期望。',
      author: '王强',
      role: '3个月前',
      source: 'Google',
      rating: 5,
    },
    {
      content:
        '我们的钢琴搬运非常成功，团队使用专业设备，动作轻柔谨慎。非常感谢他们的专业服务，价格也比其他公司便宜。',
      author: '张伟',
      role: '2个月前',
      source: 'Google',
      rating: 5,
    },
    {
      content:
        '搬家过程中非常小心地处理了我们的所有物品，特别是那些易碎的古董。工作人员非常有礼貌，效率也很高。价格比我咨询的其他公司要合理得多。',
      author: 'Sarah Chen',
      role: '1个月前',
      source: 'Google',
      rating: 5,
    },
    {
      content:
        '第二次使用他们的服务了，依然非常满意。预约简单，团队准时，服务专业。搬家通常很令人焦虑，但他们让整个过程变得轻松。',
      author: '刘阳',
      role: '3周前',
      source: 'Google',
      rating: 5,
    },
  ];

  return (
    <section id="testimonials" className="py-20 sm:py-24 bg-slate-100">
      <div className="container px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight">
            {t('testimonialsTitle')}
          </h2>
          <p className="mt-4 max-w-3xl mx-auto text-xl text-gray-600">
            {t('testimonialsSubtitle')}
          </p>
          <div className="mt-6 mx-auto w-24 h-1.5 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full"></div>
        </div>

        {/* Google Rating Overview */}
        <div className="mb-16 flex justify-center">
          <Card className="inline-flex items-center gap-4 border-2 shadow-lg">
            <CardContent className="p-6 flex items-center gap-4">
              <GoogleIcon />
              <div>
                <p className="text-lg font-bold text-gray-900">{t('testimonialsGoogle')}</p>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-bold text-yellow-500">5.0</span>
                  <StarRating rating={5} />
                  <span className="text-gray-500">({googleReviews.length}+ {t('testimonialsReviews')})</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {googleReviews.map((testimonial, index) => (
            <TestimonialCard key={index} {...testimonial} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
